import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';
import Cookies from 'js-cookie';
import { config } from '@lib/config';

const poolData = {
  UserPoolId: config.userPoolId,
  ClientId: config.clientId,
};

const userPool = new CognitoUserPool(poolData);

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export const authService = {
  // Get the current authenticated user
  getCurrentUser: (): CognitoUser | null => {
    return userPool.getCurrentUser();
  },

  // Get current session
  getCurrentSession: (): Promise<CognitoUserSession> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No current user'));
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No session'));
          return;
        }
        resolve(session);
      });
    });
  },

  // Login with email and password
  login: (email: string, password: string): Promise<AuthTokens> => {
    return new Promise((resolve, reject) => {
      const authenticationData = {
        Username: email,
        Password: password,
      };

      const authenticationDetails = new AuthenticationDetails(authenticationData);

      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const tokens: AuthTokens = {
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          };

          // Store tokens in cookies
          Cookies.set('idToken', tokens.idToken, { sameSite: 'strict' });
          Cookies.set('accessToken', tokens.accessToken, { sameSite: 'strict' });
          Cookies.set('refreshToken', tokens.refreshToken, { sameSite: 'strict' });

          resolve(tokens);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  },

  // Signup new user
  signup: (email: string, password: string, firstName: string, lastName: string): Promise<CognitoUser> => {
    return new Promise((resolve, reject) => {
      // Validate email domain
      if (email.split('@')[1] !== 'illuminativesolutions.io') {
        reject(new Error('Users may only signup using an Illuminative email address'));
        return;
      }

      const attributeList: CognitoUserAttribute[] = [
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
      ];

      userPool.signUp(email, password, attributeList, [], (err, result) => {
        if (err) {
          reject(err);
          return;
        }

        if (!result) {
          reject(new Error('Signup failed'));
          return;
        }

        resolve(result.user);
      });
    });
  },

  // Logout
  logout: (): void => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    // Clear cookies
    Cookies.remove('idToken');
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
  },

  // Verify email with code
  verifyEmail: (email: string, code: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  },

  // Parse JWT token
  parseJwt: (token: string): any => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  },

  // Get user data from JWT
  getUserDataFromToken: (idToken: string): any => {
    return authService.parseJwt(idToken);
  },

  // Forgot password - sends verification code to email
  forgotPassword: (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
        inputVerificationCode: () => {
          resolve();
        },
      });
    });
  },

  // Confirm password reset with verification code
  confirmPasswordReset: (email: string, verificationCode: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  },
};
